/**
 * @module components/maps/canada-map-container
 * @author Charles Blais <charles.blais@nrcan-nrcan.gc.ca>
 *
 * General map wrapper with Canada lambert projection.
 */

import React from 'react';

import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { MapOptions, LatLngExpression } from 'leaflet';
import 'proj4leaflet';

import { useLanguageState } from '../../contexts/language-state-provider';

// Constants
// We use basemap provided by geoappnext, these properties
// have been copied from the interface.
const projection = 'EPSG:3978'
const definition = '+proj=lcc +lat_1=49 +lat_2=77 +lat_0=49 +lon_0=-95 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
const origin = [-3.46558E7, 3.931E7]
const resolutions = [
  38364.660062653464,
  22489.62831258996,
  13229.193125052918,
  7937.5158750317505,
  4630.2175937685215,
  2645.8386250105837,
  1587.5031750063501,
  926.0435187537042,
  529.1677250021168,
  317.50063500127004,
  185.20870375074085,
  111.12522225044451,
  66.1459656252646,
  38.36466006265346,
  22.48962831258996,
  13.229193125052918,
  7.9375158750317505,
  4.6302175937685215,
];

declare global {
  interface Window { L: any; }
}

window.L = window.L || {};

type Props = {
  children?: React.ReactNode,
  className?: string,
} & MapOptions

type PropsView = {
  center?: LatLngExpression,
  zoom?: number,
}

const ChangeView = ({ center, zoom }: PropsView) => {
  const map = useMap();
  if (center !== undefined && zoom !== undefined)
    map.setView(center, zoom);
  return null;
}

/**
 * React component
 * @param param0 
 * @returns 
 */
const CanadaMapContainer = ({
  children,
  ...props
}: Props) => {
  const [lang] = useLanguageState();

  const crs = new window.L.Proj.CRS(
    projection,
    definition,
    {
      origin,
      resolutions,
    },
  );

  let url, attribution = ''
  switch (lang.abbr) {
    case "fr":
      url = "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBCT3978/MapServer/tile/{z}/{y}/{x}"
      attribution = "Sa Majesté le Rois du Chef du Canada, représentée par le ministre des Ressources naturelles"
      break
    default:
      url = "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBMT3978/MapServer/tile/{z}/{y}/{x}"
      attribution = "His Majesty the King in Right of Canada, as represented by the Minister of Natural Resources"
  }

  return (
    <>
      <MapContainer
        {...props}
        crs={crs}
        minZoom={2}
        maxZoom={resolutions.length - 1}
        attributionControl={false}
      >
        <ChangeView center={props.center} zoom={props.zoom} />
        <TileLayer
          url={url}
        //attribution={attribution}
        />
        {children}
      </MapContainer>
      <p className='attribution'>&#169; {attribution}</p>
    </>
  );
}

export default CanadaMapContainer;
